import React, { useState, useEffect, memo } from 'react';
import { Modal, ModalBody, Button, Tooltip } from 'reactstrap';
import { LiveMessage } from 'react-aria-live';
import {getSelectedProjectData} from '../utils/data';
import {useProjectData} from '../contexts/ProjectDataContextProvider';
import {useFilterContext, useFilterDispatch} from '../contexts/FilterContextProvider';
import SnapshotMap from './SnapshotMap';
import logo from '../odot-logo.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExternalLinkAlt, faAngleDoubleRight, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { faCheckSquare, faSquare } from '@fortawesome/free-regular-svg-icons';

function ProjectSnapshot() {
    const {projectData} = useProjectData();
    const filters = useFilterContext();
    const filterDispatch = useFilterDispatch();
    const selectedProjKeyNo = filters.selectedForSnapshot;
    const selectedProjectData = getSelectedProjectData(selectedProjKeyNo, projectData.projects);
    
    const [contactTT, setContactTTOpen] = useState(false);
    const toggleContactTT = () => setContactTTOpen(!contactTT);
    const [phaseTT, setPhaseTTOpen] = useState(false);
    const togglePhaseTT = () => setPhaseTTOpen(!phaseTT);
    const [mapTT, setMapTTOpen] = useState(false);
    const toggleMapTT = () => setMapTTOpen(!mapTT);
    const [modal, setToggleModal] = useState(selectedProjKeyNo.length);

    let a11yMessage = modal && selectedProjectData !== null ? "Detailed project information displayed in modal for the following project: " + selectedProjectData.FME_PROJ_NM : "Project links displayed.";

    const toggleModal = () => {
        setToggleModal(!modal);
        setContactTTOpen(false);
        setPhaseTTOpen(false);
        setMapTTOpen(false);
        if(modal) {
            filterDispatch({
                type: 'projectForSnapshot',
                payload: ""
            });
        }
    }

    useEffect(() => {
        if (filters.selectedForSnapshot !== "") {
            setToggleModal(true);
        }
    }, [filters.selectedForSnapshot]);
    
    if (selectedProjectData) {
        return (
            <Modal zIndex="" returnFocusAfterClose={true} unmountOnClose={false} isOpen={modal === true} toggle={toggleModal} className="project-snapshot">
                <ModalBody>
                    <div role="dialog" aria-labelledby="#project-snapshot-title">
                        <Button aria-label="Close" id="back-to-top" onClick={toggleModal}><FontAwesomeIcon icon={faTimes} /></Button>
                        <div className="row mx-3 mt-3">
                            <SnapshotContent project={selectedProjectData} contactTT={contactTT} toggleContactTT={toggleContactTT} phaseTT={phaseTT} togglePhaseTT={togglePhaseTT} mapTT={mapTT} toggleMapTT={toggleMapTT} />
                            <LiveMessage message={a11yMessage} aria-live="polite" />
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        );
    }
    else {
        return(
            null
        );
    }
    
}

export default memo(ProjectSnapshot);

const SnapshotContent = memo(({project, contactTT, toggleContactTT, phaseTT, togglePhaseTT, mapTT, toggleMapTT}) => {
    let projCostInfo = GetCostInfo(project);
    let projBudgetInfo = GetBudgetInfo(project);
    let projTimeInfo = GetTimeInfo(project);
    let phaseInfo = GetPhaseInfo(project, phaseTT, togglePhaseTT);
    let locationInfo = GetLocationInfo(project);
    let spentToDate = project.STIP_ADMINISTRATOR_ID && project.FME_PHASE !== "Completed" ? (project.FME_PROJ_COST_AMT && project.FME_PROJ_COST_AMT > 0 ? FormatMoney(project.FME_PROJ_COST_AMT) + "." : "No spent-to-date available.") : "";
    let quickFacts = [
        {
            label: "Estimated project cost" + (projCostInfo.length ? projCostInfo + ": " : ": "),
            value: project.STIP_ESTIMATED_PROJECT_COST && (project.FME_PHASE !== "Completed" || (project.FME_PHASE === "Completed" && !projBudgetInfo.length && !project.FME_PROJ_COST_AMT)) ? FormatMoney(project.STIP_ESTIMATED_PROJECT_COST) + "." : "",
            indent: false
        },
        {
            label: projBudgetInfo.length ? "" : "Project cost: ",
            value: project.FME_PROJ_COST_AMT && project.FME_PHASE === "Completed" ? (projBudgetInfo.length ? projBudgetInfo : FormatMoney(project.FME_PROJ_COST_AMT) + ".") + (spentToDate !== "" ? " Spent-to-date: " + spentToDate : "") : "",
            indent: false
        },
        {
            label: "Spent-to-date: ",
            value: spentToDate !== "" ? spentToDate : "",
            indent: true
        },
        {
            label: "",
            value: project.OverBudget_StatusExplanation ? project.OverBudget_StatusExplanation : "",
            indent: true
        },
        {
            label: "Goes to bid: ",
            value: project.STIP_BID_LET_DT && !project.ESTIMATED_COMPLETE_DT && !project.FME_COMPLETE_DT && project.STIP_BID_LET_DT ? FormatDate(project.STIP_BID_LET_DT) + "." : "",
            indent: false
        },
        {
            label: "Estimated completion date: ",
            value: project.ESTIMATED_COMPLETE_DT && !project.FME_COMPLETE_DT ? FormatDate(project.ESTIMATED_COMPLETE_DT) + "." : "",
            indent: false
        },
        {
            label: project.FME_COMPLETE_DT ? (projTimeInfo.length ? "" : "Completion date: ") : "",
            value: project.FME_COMPLETE_DT ? (projTimeInfo.length ? projTimeInfo : project.FME_COMPLETE_DT + ".") : "",
            indent: false
        },
        {
            label: "",
            value: project.Late_StatusExplanation ? project.Late_StatusExplanation : "",
            indent: true
        },
        {
            label: "",
            value: project.FME_SCHED_DESC ? project.FME_SCHED_DESC: "",
            indent: false
        },
        {
            label: "Project type: ",
            value: project.FME_PROJ_TYPE ? project.FME_PROJ_TYPE + "." : "",
            indent: false
        },
        {
            label: "",
            value: project.FME_ADMINISTRATOR ? project.FME_ADMINISTRATOR : "",
            indent: false
        }
    ];
    let hasQuickFacts = false;
    for (let quickFact in quickFacts) {
        if (quickFacts[quickFact].value.length > 0) {
            hasQuickFacts = true;
        } 
    }
    
    return (
        <>
            <div className="col-md-8">
                <h2 id="project-snapshot-title">{project.FME_PROJ_NM}</h2>
                <p className="project-no">
                    Project number: {project.FME_PROJ_KEY_NO}
                </p>
                <hr/>
                
                <h3 className="mt-4 mb-3">Description</h3>
                <div className="mb-3" dangerouslySetInnerHTML={{ __html: project.FME_PROJ_DESC }}></div>
                
                {project.FME_PROJ_URL ? 
                    <p>
                        <a href={"" + project.FME_PROJ_URL + ""}><FontAwesomeIcon icon={faExternalLinkAlt} /> Additional project information, benefits and more</a>
                    </p> : ""}
                {project.FME_PHASE === "Construction" ? 
                    <p>
                        <a href="https://www.tripcheck.com"><FontAwesomeIcon icon={faExternalLinkAlt} /> Visit TripCheck for real-time traffic conditions</a>
                    </p> : ""}
                {(project.STIP_ADMINISTRATOR_ID === "ODOT" || project.STIP_ADMINISTRATOR_ID === "ODOT/LOCAL") && project.STIP_MNTR_CD === "PSEDOC" && CompareDate(project.STIP_BID_LET_DT) ? 
                    <p>
                        <a href="https://www.oregon.gov/ODOT/Business/Procurement/Pages/Letting_Schedules.aspx"><FontAwesomeIcon icon={faExternalLinkAlt} /> More information on contracting opportunities</a>
                    </p> : ""}

                <h3 className="mt-4 mb-3">Contact Information</h3>
                {project.FME_CONTACT1_NAME ? 
                    <p>
                        {project.FME_CONTACT1_NAME.indexOf("Ask ODOT") !== -1 ? <a href="https://www.oregon.gov/odot/Pages/Ask-ODOT.aspx">Ask ODOT</a> : 
                            <a href={"mailto:" + project.FME_CONTACT1_EMAIL.trim() + "?subject=" + project.FME_PROJ_NM.replace("&", "%26") + ", project number: " + project.FME_PROJ_KEY_NO + " [from Transportation Project Tracker]"}>{project.FME_CONTACT1_NAME}</a>
                        }
                        {project.FME_CONTACT1_NAME.indexOf("Ask ODOT") !== -1 ? <>
                            <Button style={{marginTop: "-5px"}} className="mr-0 p-0" color="none" id="askODOTInfo"><FontAwesomeIcon icon={faQuestionCircle} className="phase-info-icon" /></Button>
                            <Tooltip placement="bottom" trigger="hover click" isOpen={contactTT} target="askODOTInfo" toggle={toggleContactTT}>Ask ODOT representatives answer questions on the spot or refer you to a broad range of contacts within the agency.</Tooltip>
                        </> : ""}<br/>
                        {project.FME_CONTACT1_TITLE}{project.FME_CONTACT1_TITLE ? <br/> : ""}
                        {project.FME_CONTACT1_PHONE}
                    </p> : ""}
                {project.FME_CONTACT2_NAME ? 
                    <p>
                        <a href={"mailto:" + project.FME_CONTACT2_EMAIL.trim() + "?subject=" + project.FME_PROJ_NM.replace("&", "%26") + ", project number: " + project.FME_PROJ_KEY_NO + " [from Transportation Project Tracker]"}>{project.FME_CONTACT2_NAME}</a><br/>
                        {project.FME_CONTACT2_TITLE}{project.FME_CONTACT2_TITLE ? <br/> : ""}
                        {project.FME_CONTACT2_PHONE}
                    </p> : ""}

                {hasQuickFacts && <h3 className="mt-4 mb-3">Quick Facts</h3>}
                <ul>
                    {quickFacts && quickFacts.map(quickFact => {
                        if (quickFact.value.length) {
                            if (quickFact.indent) {
                                return (
                                    <ul key={quickFact.label + quickFact.value}>
                                        <li>{quickFact.label}{quickFact.value}</li>
                                    </ul>
                                )
                            }
                            return (
                                <li key={quickFact.label + quickFact.value}>{quickFact.label}{quickFact.value}</li>
                            );
                        }
                        else {
                           return null; 
                        }
                    })}
                </ul>

                {(project.FME_PHASE && project.FME_PHASE !== "Other") ? 
                    <>
                        <h3 className="mt-4 mb-3">Project Phase</h3>
                        <div className="project-phase">
                            {phaseInfo}
                        </div>
                    </> : ""}

                {locationInfo.hasLocation ? 
                    <>
                        <h3 className="mt-4 mb-3">Project Location</h3>
                        <ul>
                            {Object.values(locationInfo).map(({value, display}) => {
                                if (typeof value !== 'boolean' && value !== "") {
                                    return (
                                        <li key={display}>{display}<strong>{value}</strong></li>
                                    )
                                }
                                else {
                                    return null;
                                }
                            })}
                        </ul>
                    </> : ""}
            </div>
            <div id="mini-map" className="col-md-4">
                <div className="selectedMapHeader">
                    Selected Project Map
                    <Button style={{marginTop: "-4px"}} className="mr-0 p-0" color="none" id="selectedProjMap"><FontAwesomeIcon icon={faQuestionCircle} className="map-info-icon" /></Button>
                    <Tooltip placement="bottom" id="map-tooltip" trigger="hover click" isOpen={mapTT} target="selectedProjMap" toggle={toggleMapTT}>The map below includes all mapped locations for the selected project.</Tooltip>
                </div>
                <SnapshotMap initDefinitionExpression={"FME_PROJ_KEY_NO = '" + project.FME_PROJ_KEY_NO + "'"} />
            </div>
            <div className="snapshot-help">
                <a href="https://www.oregon.gov/ODOT/Projects/Pages/UsingTPT.aspx" rel="noreferrer"><FontAwesomeIcon icon={faQuestionCircle} aria-label="Question" />&nbsp;Learn more about the data</a>
            </div>
            <img alt="ODOT Logo" src={logo} style={{margin: "0px 0px 10px", height: "50px"}} />
        </>
    );
});

function FormatMoney(amount) {
    if (amount) {
        return "$" + Math.abs(parseFloat(amount)).toFixed(2).toString().replace(/(\d)(?=(\d{3})+\b)/g, "$1,");
    }
    return null;
}

function FormatDate(date) {
    let newDate = "";
    if (date) {
        newDate = date.substring(4, 6) + "/" + date.substring(6, 8) + "/" + date.substring(0, 4);
    }
    return newDate;
}

function CompareDate(date) {
    if (date) {
        date = new Date(FormatDate(date.toString()));
        let currentDate = new Date();
        let oneYearAhead = new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
        return date <= oneYearAhead;
    }
    return false;
}

function GetCostInfo(project) {
    let costInfo = "";
    switch(project.STIP_MNTR_CD) {
        case "PSEDOC":
            costInfo += " through construction";
            break;
        case "ROWACQ":
        case "FNLPLN":
        case "UTLREL":
            costInfo += " through pre-construction";
            break;
        case "ENVDOC":
        case "PLAN":
        case "STUDY":
            costInfo += " for planning";
            break;
        default:
            break;
    }
    return costInfo;
}

function GetBudgetInfo(project) {
    let budgetInfo = "";
    switch(project.OverBudget) {
        case "TRUE":
            budgetInfo += "Over budget, project cost of " + FormatMoney(project.FME_PROJ_COST_AMT) + " was more than 10% over original estimated cost (" + FormatMoney(project.OverBudget_Amt) + " over).";
            break;
        case "FALSE":
            if (project.OverBudget_Amt >= 0) {
                budgetInfo += "On budget, project cost of " + FormatMoney(project.FME_PROJ_COST_AMT) + " was within 10% of original estimated cost (" + FormatMoney(project.OverBudget_Amt) + " over).";
            }
            else {
                budgetInfo += "On budget, project cost of " + FormatMoney(project.FME_PROJ_COST_AMT) + " was within 10% of original estimated cost (" + FormatMoney(project.OverBudget_Amt) + " under).";
            }
            break;
        default:
            break;
    }
    return budgetInfo;
}

function GetTimeInfo(project) {
    let timeInfo = "";
    switch(project.Late) {
        case "TRUE":
            timeInfo += "Late, project was completed on " + FormatDate(project.FME_COMPLETE_DT) + ", more than 90 days past the original estimated completion date (" + project.DaysLate + " days late).";
            break;
        case "FALSE":
            timeInfo += "On time, project was completed on " + FormatDate(project.FME_COMPLETE_DT) + ", within 90 days of original estimated completion date (" + Math.abs(project.DaysLate) + " day" + (Math.abs(project.DaysLate) === 1 ? "" : "s") + (project.DaysLate >= 0 ? " over" : " early") + ").";
            break;
        default:
            timeInfo += "Completion date: " + FormatDate(project.FME_COMPLETE_DT) + ".";
            break;
    }
    return timeInfo;
}

function GetPhaseInfo(project, phaseTT, togglePhaseTT) {
    let phaseInfo = <></>;
    switch(project.FME_PHASE) {
        case "Planning":
            let planningPhaseInfo = <></>;
            switch(project.STIP_MNTR_CD) {
                case "ENVDOC":
                    planningPhaseInfo = <>
                        <div><FontAwesomeIcon icon={faSquare} pull="left" fixedWidth /> Design</div>
                        <div><FontAwesomeIcon icon={faSquare} pull="left" fixedWidth /> Construction</div>
                        <div><FontAwesomeIcon icon={faSquare} pull="left" fixedWidth /> Project Completed</div>
                    </>;
                    break;
                default:    //PLAN, STUDY, null
                    planningPhaseInfo = <></>;
            }
            phaseInfo = <>
                <div className="active-phase">
                    <FontAwesomeIcon icon={faAngleDoubleRight} pull="left" fixedWidth /> Planning
                    <Button className="mr-0" color="none" id="phaseInfo"><FontAwesomeIcon icon={faQuestionCircle} className="phase-info-icon" /></Button>
                    <Tooltip placement="bottom" trigger="hover click" isOpen={phaseTT} target="phaseInfo" toggle={togglePhaseTT}>Plan or study for future projects</Tooltip>
                </div>
                {planningPhaseInfo}
            </>;
            break;
        case "Design":
            let designPhaseInfo = <></>;
            switch(project.STIP_MNTR_CD) {
                case "PSEDOC":
                    designPhaseInfo = <Tooltip placement="bottom" trigger="hover click" isOpen={phaseTT} target="phaseInfo" toggle={togglePhaseTT}>
                        Project is in design and is scheduled for construction
                    </Tooltip>;
                    break;
                case null:
                    designPhaseInfo = <Tooltip placement="bottom" trigger="hover click" isOpen={phaseTT} target="phaseInfo" toggle={togglePhaseTT}>
                        Project is in the design phase
                    </Tooltip>;
                    break;
                default:    //ROWACQ, UTLREL, FNLPLN
                    designPhaseInfo = <Tooltip placement="bottom" trigger="hover click" isOpen={phaseTT} target="phaseInfo" toggle={togglePhaseTT}>
                        Project may include buying land, moving utilities or completing final plans
                    </Tooltip>;
            }
            phaseInfo = <>
                <div><FontAwesomeIcon icon={faCheckSquare} pull="left" fixedWidth /> Planning</div>
                <div className="active-phase">
                    <FontAwesomeIcon icon={faAngleDoubleRight} pull="left" fixedWidth /> Design
                    {project.STIP_MNTR_CD === "ROWACQ" || project.STIP_MNTR_CD === "UTLREL" || project.STIP_MNTR_CD === "FNLPLN" ? 
                        <span className="phase-explanation"> - Project funded through pre-construction</span> : ""}
                    
                    <Button className="mr-0" color="none" id="phaseInfo"><FontAwesomeIcon icon={faQuestionCircle} className="phase-info-icon" /></Button>
                    {designPhaseInfo}
                </div>
                <div><FontAwesomeIcon icon={faSquare} pull="left" fixedWidth /> Construction</div>
                <div><FontAwesomeIcon icon={faSquare} pull="left" fixedWidth /> Project Completed</div>  
            </>;
            break;
        case "Construction":
            phaseInfo = <>
                <div><FontAwesomeIcon icon={faCheckSquare} pull="left" fixedWidth /> Planning</div>
                <div><FontAwesomeIcon icon={faCheckSquare} pull="left" fixedWidth /> Design</div>
                <div className="active-phase">
                    <FontAwesomeIcon icon={faAngleDoubleRight} pull="left" fixedWidth /> Construction
                    <Button className="mr-0" color="none" id="phaseInfo"><FontAwesomeIcon icon={faQuestionCircle} className="phase-info-icon" /></Button>
                    <Tooltip placement="bottom" trigger="hover click" isOpen={phaseTT} target="phaseInfo" toggle={togglePhaseTT}>Project is under construction</Tooltip>
                </div>
                <div><FontAwesomeIcon icon={faSquare} pull="left" fixedWidth /> Project Completed</div>
            </>;
            break;
        case "Completed":
            phaseInfo = <div className="active-phase">
                <FontAwesomeIcon icon={faCheckSquare} pull="left" fixedWidth /> Project Completed
                <Button className="mr-0" color="none" id="phaseInfo"><FontAwesomeIcon icon={faQuestionCircle} className="phase-info-icon" /></Button>
                <Tooltip placement="bottom" trigger="hover click" isOpen={phaseTT} target="phaseInfo" toggle={togglePhaseTT}>Project is complete or open to traffic</Tooltip>
            </div>;
            break;
        default:
            phaseInfo = <></>;
    }

    return phaseInfo;
}

function GetLocationInfo(project) {
    let locationInfo = {
        hasLocation: {
            value: false
        },
        route: {
            display: "Route: ",
            value: ""
        },
        city: {
            display: "City: ",
            value: ""
        },
        county: {
            display: "County: ",
            value: ""
        },
        region: {
            display: "ODOT Region: ",
            value: ""
        },
        act: {
            display: "Area Commission on Transportation: ",
            value: ""
        },
        mpo: {
            display: "Metropolitan Planning Organization: ",
            value: ""
        },
        usCngrDist: {
            display: "US Congressional District: ",
            value: ""
        },
        repDist: {
            display: "State Representative District: ",
            value: ""
        },
        senDist: {
            display: "State Senate District: ",
            value: ""
        }
    }

    if (project.FME_ROUTE) {
        locationInfo.hasLocation.value = true;
        locationInfo.route.value = project.FME_ROUTE;
    }
    if (project.FME_CITY) {
        locationInfo.hasLocation.value = true;
        locationInfo.city.value = project.FME_CITY;
    }
    if (project.FME_COUNTY) {
        locationInfo.hasLocation.value = true;
        locationInfo.county.value = project.FME_COUNTY;
    }
    if (project.FME_REGION) {
        locationInfo.hasLocation.value = true;
        locationInfo.region.value = project.FME_REGION === "6" ? "Statewide" : project.FME_REGION;
    }
    if (project.FME_ACT) {
        locationInfo.hasLocation.value = true;
        locationInfo.act.value = project.FME_ACT;
    }
    if (project.FME_MPO) {
        locationInfo.hasLocation.value = true;
        locationInfo.mpo.value = project.FME_MPO;
    }
    if (project.FME_US_CNGR_DIST) {
        locationInfo.hasLocation.value = true;
        locationInfo.usCngrDist.value = project.FME_US_CNGR_DIST;
    }
    if (project.FME_STATE_REP_DIST) {
        locationInfo.hasLocation.value = true;
        locationInfo.repDist.value = project.FME_STATE_REP_DIST;
    }
    if (project.FME_STATE_SEN_DIST) {
        locationInfo.hasLocation.value = true;
        locationInfo.senDist.value = project.FME_STATE_SEN_DIST;
    }

    return locationInfo;
}